import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LoginRegisterModal from "./LoginRegisterModal";
import ProductShareModal from "./ProductShareModal";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { fetchShopJson, SHOP_IMAGE_BASE } from "./shopApi";
import { pruneWishlist, readWishlist, toggleWishlist, writeWishlist } from "./wishlistStorage";
import "../index.css";
import SeoManager from "../SeoManager";
import { toAbsoluteImageUrl } from "../seo";

const RECENTLY_VIEWED_KEY = "asher_recently_viewed_products";
const MAX_RECENTLY_VIEWED = 12;

const CATEGORY_RULES = [
  { label: "Rings", patterns: [/\brings?\b/i] },
  { label: "Bracelets", patterns: [/\bbracelets?\b/i] },
  { label: "Necklaces", patterns: [/\bnecklaces?\b/i] },
  { label: "Earrings", patterns: [/\bearrings?\b/i] },
  { label: "Bangles", patterns: [/\bbangles?\b/i] },
  { label: "Pendants", patterns: [/\bpendants?\b/i] },
  { label: "Anklets", patterns: [/\banklets?\b/i] },
  { label: "Chains", patterns: [/\bchains?\b/i] },
];

function getDisplayCategory(product) {
  const source = `${product?.name || ""} ${product?.description || ""}`;

  for (const rule of CATEGORY_RULES) {
    if (rule.patterns.some((pattern) => pattern.test(source))) {
      return rule.label;
    }
  }

  return "Other Finds";
}

function formatPrice(value) {
  const amount = Number.parseFloat(value || 0);
  if (Number.isNaN(amount)) {
    return "0.00";
  }

  return amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCount(value) {
  const count = Number.parseInt(value || 0, 10);
  if (Number.isNaN(count) || count < 0) {
    return "0";
  }

  return count.toLocaleString("en-PH");
}

function getKaratLabel(product) {
  const source = `${product?.category_name || ""} ${product?.name || ""}`.toUpperCase();
  const match = source.match(/\b(\d{1,2})\s*K\b/);
  return match ? `${match[1]}K` : "Other";
}

function isVideoUrl(url) {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url || "");
}

function getInitials(firstname, lastname) {
  const first = (firstname || "").trim().charAt(0);
  const last = (lastname || "").trim().charAt(0);
  return `${first}${last}`.trim().toUpperCase() || "AE";
}

function readRecentlyViewed() {
  try {
    const raw = window.localStorage.getItem(RECENTLY_VIEWED_KEY);
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeRecentlyViewed(productId) {
  try {
    const ids = readRecentlyViewed().filter((id) => String(id) !== String(productId));
    ids.unshift(productId);
    window.localStorage.setItem(
      RECENTLY_VIEWED_KEY,
      JSON.stringify(ids.slice(0, MAX_RECENTLY_VIEWED))
    );
  } catch {
    // Ignore storage issues for suggestion history.
  }
}

function SuggestionSection({ title, subtitle, products, onOpenProduct }) {
  if (!products.length) {
    return null;
  }

  return (
    <section className="suggestion-section">
      <div className="suggestion-section__heading">
        <p className="category-section__eyebrow">{subtitle}</p>
        <h2>{title}</h2>
      </div>

      <div className="suggestion-grid">
        {products.map((item) => (
          <article key={item.id} className="suggestion-card">
            <button
              type="button"
              className="suggestion-card__image"
              onClick={() => onOpenProduct(item.id)}
            >
              {isVideoUrl(item.imageUrl) ? (
                <video src={`${SHOP_IMAGE_BASE}${item.imageUrl}`} muted playsInline preload="metadata" />
              ) : (
                <img src={`${SHOP_IMAGE_BASE}${item.imageUrl}`} alt={item.name} />
              )}
            </button>
            <button
              type="button"
              className="suggestion-card__title"
              onClick={() => onOpenProduct(item.id)}
            >
              {item.name}
            </button>
            {(item.brand || "").trim() && (
              <p className="suggestion-card__brand">{(item.brand || "").trim()}</p>
            )}
            <p className="suggestion-card__price">PHP {formatPrice(item.pricePHP)}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function ProductDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems, addToCart, loadServerCart, openSidebar } = useCart();

  const [product, setProduct] = useState(null);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const pageTopRef = useRef(null);

  useEffect(() => {
    const scrollTarget = () => {
      if (pageTopRef.current && typeof pageTopRef.current.scrollIntoView === "function") {
        pageTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.requestAnimationFrame(scrollTarget);
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setWishlistIds(readWishlist());

    Promise.all([
      fetchShopJson(`/get_product_details.php?id=${encodeURIComponent(id)}`),
      fetchShopJson("/get_products.php"),
    ])
      .then(([detailResponse, catalogResponse]) => {
        if (!isMounted) {
          return;
        }

        if (detailResponse?.status === "success" && detailResponse.data) {
          setProduct(detailResponse.data);
          setActiveImage(detailResponse.data.images?.[0] || detailResponse.data.imageUrl || "");
          writeRecentlyViewed(detailResponse.data.id);
          if (Number(detailResponse.data.stocks || 0) <= 0) {
            setWishlistIds(
              writeWishlist(
                readWishlist().filter(
                  (wishlistId) => String(wishlistId) !== String(detailResponse.data.id)
                )
              )
            );
          }
        } else {
          setProduct(null);
        }

        setCatalogProducts(Array.isArray(catalogResponse) ? catalogResponse : []);
      })
      .catch((error) => {
        console.error(error);
        if (isMounted) {
          setProduct(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!catalogProducts.length) {
      return;
    }
    const availableIds = catalogProducts
      .filter((item) => Number(item.stocks || 0) > 0)
      .map((item) => item.id);
    setWishlistIds(pruneWishlist(availableIds));
  }, [catalogProducts]);

  const inCart = useMemo(
    () => cartItems.some((item) => String(item.id) === String(id)),
    [cartItems, id]
  );

  const displayCategory = useMemo(() => getDisplayCategory(product || {}), [product]);
  const displayBrand = useMemo(() => ((product?.brand || "").trim()), [product]);
  const displayKarat = useMemo(() => getKaratLabel(product || {}), [product]);
  const isOutOfStock = useMemo(() => Number(product?.stocks || 0) <= 0, [product]);
  const isWishlisted = useMemo(
    () => wishlistIds.includes(String(product?.id || "")),
    [wishlistIds, product]
  );

  const suggestionCandidates = useMemo(
    () =>
      catalogProducts
        .filter((item) => String(item.id) !== String(id))
        .map((item) => ({
          ...item,
          displayCategory: getDisplayCategory(item),
          displayBrand: (item.brand || "").trim(),
        })),
    [catalogProducts, id]
  );

  const similarProducts = useMemo(() => {
    const exactMatches = suggestionCandidates.filter(
      (item) =>
        item.displayCategory === displayCategory ||
        (displayBrand && item.displayBrand === displayBrand)
    );

    const fallbackMatches = suggestionCandidates.filter(
      (item) => !exactMatches.some((candidate) => String(candidate.id) === String(item.id))
    );

    return [...exactMatches, ...fallbackMatches].slice(0, 8);
  }, [suggestionCandidates, displayCategory, displayBrand]);

  const recentlyViewedProducts = useMemo(() => {
    const ids = readRecentlyViewed().filter((recentId) => String(recentId) !== String(id));
    const lookup = new Map(suggestionCandidates.map((item) => [String(item.id), item]));

    return ids
      .map((recentId) => lookup.get(String(recentId)))
      .filter(Boolean)
      .slice(0, 8);
  }, [suggestionCandidates, id]);

  const ensureProductInCart = async () => {
    if (!product || inCart || isOutOfStock) {
      return;
    }

    await addToCart({
      ...product,
      qty: 1,
    });
  };

  const handleAddToCart = async () => {
    if (!product) {
      return;
    }

    if (!user) {
      setPendingAction("cart");
      setShowModal(true);
      return;
    }

    if (isOutOfStock) {
      return;
    }

    await ensureProductInCart();
    openSidebar();
  };

  const handleBuyNow = async () => {
    if (!product) {
      return;
    }

    if (isOutOfStock) {
      return;
    }

    if (!user) {
      setPendingAction("checkout");
      setShowModal(true);
      return;
    }

    await ensureProductInCart();
    navigate("/checkout");
  };

  const onAuthSuccess = async () => {
    await loadServerCart();

    if (pendingAction === "cart") {
      await ensureProductInCart();
      openSidebar();
    }

    if (pendingAction === "checkout") {
      await ensureProductInCart();
      navigate("/checkout");
    }

    setPendingAction(null);
  };

  const openSuggestedProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleWishlistToggle = () => {
    if (!product || isOutOfStock) {
      return;
    }

    setWishlistIds(toggleWishlist(product.id));
  };

  if (loading) {
    return <div className="product-detail-page"><p>Loading product details...</p></div>;
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-empty">
          <h2>Product not found</h2>
          <button type="button" className="catalog-reset-btn" onClick={() => navigate("/")}>
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="product-detail-page" ref={pageTopRef}>
        <SeoManager
          title={product.name}
          description={product.description || `${product.name} by Asher Jewels PH`}
          path={`/product/${product.id}`}
          image={toAbsoluteImageUrl(`${SHOP_IMAGE_BASE}${activeImage || product.imageUrl}`)}
          type="product"
          keywords={`${product.name}, ${displayCategory}, ${displayKarat}, ${product.category_name || "gold jewelry"}, Asher Jewels PH`}
          schema={{
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            image: (product.images || []).map((imagePath) => toAbsoluteImageUrl(`${SHOP_IMAGE_BASE}${imagePath}`)),
            description: product.description || product.name,
            sku: product.product_code || undefined,
            brand: displayBrand ? { "@type": "Brand", name: displayBrand } : { "@type": "Brand", name: "Asher Jewels PH" },
            category: displayCategory,
            offers: {
              "@type": "Offer",
              priceCurrency: "PHP",
              price: Number(product.pricePHP || 0),
              availability: isOutOfStock
                ? "https://schema.org/OutOfStock"
                : "https://schema.org/InStock",
              url: `https://shop.asherjewels.com.ph/product/${product.id}`,
            },
          }}
        />
        <button type="button" className="product-detail-back" onClick={() => navigate(-1)}>
          Back
        </button>

        <section className="product-detail-card">
          <div className="product-detail-gallery">
            <div className="product-detail-main-image">
              {isVideoUrl(activeImage || product.imageUrl) ? (
                <video src={`${SHOP_IMAGE_BASE}${activeImage || product.imageUrl}`} controls playsInline preload="metadata" />
              ) : (
                <img src={`${SHOP_IMAGE_BASE}${activeImage || product.imageUrl}`} alt={product.name} />
              )}
            </div>
            <div className="product-detail-thumbs">
              {(product.images || []).map((imageUrl) => (
                <button
                  type="button"
                  key={imageUrl}
                  className={`product-detail-thumb ${activeImage === imageUrl ? "is-active" : ""}`}
                  onClick={() => setActiveImage(imageUrl)}
                >
                  {isVideoUrl(imageUrl) ? (
                    <video src={`${SHOP_IMAGE_BASE}${imageUrl}`} muted playsInline preload="metadata" />
                  ) : (
                    <img src={`${SHOP_IMAGE_BASE}${imageUrl}`} alt={product.name} />
                  )}
                </button>
              ))}
            </div>
            {Array.isArray(product.videos) && product.videos.length > 0 && (
              <div className="product-detail-video-section">
                <p className="category-section__eyebrow">Product Videos</p>
                <div className="product-detail-video-grid">
                  {product.videos.map((videoUrl) => (
                    <video
                      key={videoUrl}
                      className="product-detail-video"
                      controls
                      preload="metadata"
                    >
                      <source src={`${SHOP_IMAGE_BASE}${videoUrl}`} />
                      Your browser does not support the video tag.
                    </video>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="product-detail-info">
            <p className="category-section__eyebrow">Product Details</p>
            <h1>{product.name}</h1>
            {displayBrand && <p className="product-detail-brand">{displayBrand}</p>}
            <p className="product-detail-price">PHP {formatPrice(product.pricePHP)}</p>
            <div className="product-detail-status-row">
              <span className="product-detail-chip">{displayKarat}</span>
              <span className={`product-detail-chip ${isOutOfStock ? "is-out" : "is-stocked"}`}>
                {isOutOfStock ? "No Stock" : `${Number(product.stocks || 0)} in stock`}
              </span>
            </div>
            <div className="product-detail-view-count" aria-label="Product views">
              <span className="product-detail-view-count__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                  <path
                    d="M2.75 12s3.4-5.75 9.25-5.75S21.25 12 21.25 12s-3.4 5.75-9.25 5.75S2.75 12 2.75 12Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="12" r="2.85" fill="none" stroke="currentColor" strokeWidth="1.7" />
                </svg>
              </span>
              <span>{formatCount(product.view_count)} recent views</span>
            </div>
            <p className="product-detail-description">{product.description}</p>

            <div className="product-detail-meta">
              <div>
                <span>Jewelry Type</span>
                <strong>{displayCategory}</strong>
              </div>
              <div>
                <span>Pricing Category</span>
                <strong>{product.category_name || "Not set"}</strong>
              </div>
              <div>
                <span>Product Code</span>
                <strong>{product.product_code || "Pending"}</strong>
              </div>
              <div>
                <span>Weight</span>
                <strong>{product.grams || "0.00"} g</strong>
              </div>
              <div>
                <span>Availability</span>
                <strong>{isOutOfStock ? "Sold / No stock" : "Available"}</strong>
              </div>
            </div>

            {product.accountExecutive ? (
              <div className="product-detail-seller">
                <p className="category-section__eyebrow">Account Executive</p>
                <button
                  type="button"
                  className="product-detail-seller__card"
                  onClick={() => navigate(`/account-executive/${product.accountExecutive.id}`)}
                >
                  <div className="product-detail-seller__avatar">
                    {product.accountExecutive.photo ? (
                      <img
                        src={`${SHOP_IMAGE_BASE}${product.accountExecutive.photo}`}
                        alt={`${product.accountExecutive.firstname} ${product.accountExecutive.lastname}`}
                      />
                    ) : (
                      <span>{getInitials(product.accountExecutive.firstname, product.accountExecutive.lastname)}</span>
                    )}
                  </div>
                  <div className="product-detail-seller__body">
                    <strong>{`${product.accountExecutive.firstname || ""} ${product.accountExecutive.lastname || ""}`.trim()}</strong>
                    <span>{product.accountExecutive.designation || "Account Executive"}</span>
                    <div className="product-detail-seller__stats">
                      <span>{product.accountExecutive.available_items || 0} available</span>
                      <span>{product.accountExecutive.total_items || 0} uploaded</span>
                    </div>
                  </div>
                  <span className="product-detail-seller__link">View collection</span>
                </button>
              </div>
            ) : null}

            <div className="product-detail-actions">
              <button
                type="button"
                className="share-action"
                onClick={() => setShowShareModal(true)}
              >
                <span className="share-action__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                    <path d="M15.5 8.5a3 3 0 1 0-2.74-4.22L8.8 6.47a3 3 0 1 0 0 11.06l3.96 2.19A3 3 0 1 0 14 17.5l-3.97-2.2a3.07 3.07 0 0 0 0-1.6L14 11.5a3 3 0 1 0 1.5-3Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span>Share</span>
              </button>
              <button
                type="button"
                className={`wishlist-action ${isWishlisted ? "is-active" : ""}`}
                onClick={handleWishlistToggle}
                disabled={isOutOfStock}
              >
                {isWishlisted ? "Saved to Wishlist" : "Add to Wishlist"}
              </button>
              <button
                type="button"
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={isOutOfStock || inCart}
              >
                {isOutOfStock ? "No Stock" : inCart ? "In Cart" : "Add to Cart"}
              </button>
              <button
                type="button"
                className="checkout-btn"
                onClick={handleBuyNow}
                disabled={isOutOfStock}
              >
                {isOutOfStock ? "Unavailable" : "Buy Now"}
              </button>
            </div>
          </div>
        </section>

        <SuggestionSection
          title="You May Also Like"
          subtitle="Smart Suggestions"
          products={similarProducts}
          onOpenProduct={openSuggestedProduct}
        />

        <SuggestionSection
          title="Recently Viewed"
          subtitle="Your Browsing History"
          products={recentlyViewedProducts}
          onOpenProduct={openSuggestedProduct}
        />
      </div>

      {showShareModal && (
        <ProductShareModal
          product={product}
          onClose={() => setShowShareModal(false)}
        />
      )}

      <LoginRegisterModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setPendingAction(null);
        }}
        onSuccess={onAuthSuccess}
      />
    </>
  );
}
